/**
 * CallTrackingMetrics Safe-Fetch Wrapper (Story 22.3, AC #5, AC #7)
 *
 * Thin shim over the existing `safeFetch`. The shim's job is THREE-fold:
 *
 *  1. Inject the `Authorization: Basic <base64(ACCESS_KEY:SECRET_KEY)>`
 *     header (CTM v1 auth shape per the [API Users Guide](https://github.com/calltracking/calltracking.github.io/blob/master/api_users_guide.md)
 *     verified 2026-05-15) on every request. CTM is the FIRST adapter
 *     in our library to require TWO secrets in one credential bundle.
 *  2. Redact the dual-secret payload from the error message before any
 *     structured-log emission, Sentry breadcrumb, or adapter-error
 *     surface (Round 3 Red Team Attack #3). Targets:
 *       - The Base64-encoded `Basic <base64>` substring.
 *       - The plaintext `ACCESS_KEY:SECRET_KEY` form.
 *       - Each individual secret value.
 *       - URL-encoded variants of all three forms above.
 *  3. On HTTP 429, mark the error retryable=true so the queue's
 *     exponential backoff kicks in with the conservative 60-sec
 *     cooldown contract per AC #7. CTM does NOT document a
 *     `Retry-After` header (verified web search 2026-05-15) so the
 *     queue handles the delay rather than blocking here.
 *
 * **Body sanity check (defense — carried forward from CallRail 22.1 +
 * Semrush 20.4 + Moz 20.7):** the request body MUST NOT contain either
 * secret value. CTM sends the secrets via the Authorization header
 * ONLY; if a future helper accidentally stuffs a secret into the body,
 * this synchronous TypeError catches the misuse before the request
 * goes out.
 *
 * **Round 3 Red Team Attack #3:** without explicit sanitization at
 * the boundary, secrets can leak into:
 *  - Cloudflare Workers' built-in request logs.
 *  - Sentry breadcrumbs / error-tracking stacks.
 *  - Our own structured-log events.
 *  - Customer-visible adapter error messages on a 5xx surface (where
 *    the upstream provider may echo the request headers in the body).
 *
 * Defense at the boundary > defense at every consumer.
 */

import { safeFetch } from "../safe-fetch";
import type { AdapterResult } from "../adapter";
import { buildCtmBasicAuthHeader } from "./auth";
import { sanitizeCtmErrorMessage } from "./errors";

/** Per-call timeout (15s — matches CallRail / Ahrefs / Semrush / Moz). */
const CTM_TIMEOUT_MS = 15_000;

/**
 * 429 fallback cooldown (60 seconds). CTM does NOT document a
 * `Retry-After` header (verified 2026-05-15 — `postman.calltrackingmetrics.com/`
 * + `github.com/calltracking/ctm-php` are both silent on rate-limit
 * headers). Community reports indicate ~300 req/min/account soft cap.
 * 60 sec gives the bucket time to refill. Surfaced as a constant for
 * the queue layer + tests; the wrapper itself does NOT sleep inline
 * (Cloudflare Workers CPU-time / wall-clock budget defense — see
 * CallRail 22.1 Pass 1 review).
 */
export const CTM_429_COOLDOWN_MS = 60_000;

/**
 * Per-call options for the safe-fetch wrapper. `integrationId` is for
 * log enrichment only — no per-integration cooldown tracker.
 */
export interface SafeFetchCtmOptions {
  readonly integrationId?: string;
  readonly now?: () => number;
}

/**
 * `safeFetch` wrapper that:
 *
 *  1. Injects the `Authorization: Basic <base64(accessKey:secretKey)>`
 *     header alongside any caller-provided headers.
 *  2. On error, sanitizes the error message via `sanitizeCtmErrorMessage`
 *     so the literal dual-secret payload is redacted from any surface
 *     downstream code might log.
 *
 * @param url - The full CTM v1 URL.
 * @param accessKey - The plaintext Access Key. In scope ONLY for this call.
 * @param secretKey - The plaintext Secret Key. In scope ONLY for this call.
 * @param init - Standard `RequestInit` for the underlying fetch.
 * @param options - Optional integration context for log enrichment.
 */
export async function safeFetchCtm(
  url: string,
  accessKey: string,
  secretKey: string,
  init?: RequestInit,
  _options: SafeFetchCtmOptions = {},
): Promise<AdapterResult<Response>> {
  // Body-leak defense — synchronous TypeError if a caller accidentally
  // puts either secret in the body. Cheap check; only trips on actual
  // misuse. Threshold 20 chars per key matches the Zod connect-schema
  // lower bound; below 20 we skip (too-short test stubs would false-
  // positive on shared substrings).
  if (typeof init?.body === "string") {
    if (accessKey.length >= 20 && init.body.includes(accessKey)) {
      throw new TypeError(
        "safeFetchCtm: init.body must not contain the Access Key. Use the Authorization: Basic header only.",
      );
    }
    if (secretKey.length >= 20 && init.body.includes(secretKey)) {
      throw new TypeError(
        "safeFetchCtm: init.body must not contain the Secret Key. Use the Authorization: Basic header only.",
      );
    }
  }

  // Inject `Authorization: Basic <base64>` header; preserve any
  // caller-provided headers (e.g. Accept). Use the Headers class so
  // HTTP-standard case-insensitive key semantics (RFC 7230 §3.2)
  // apply — a caller passing `{ authorization: "..." }` (lowercase)
  // would otherwise produce a merged object containing both keys.
  const headers = new Headers();
  headers.set("Accept", "application/json");
  if (init?.headers) {
    if (init.headers instanceof Headers) {
      init.headers.forEach((value, key) => headers.set(key, value));
    } else if (Array.isArray(init.headers)) {
      for (const [key, value] of init.headers) {
        headers.set(key, value);
      }
    } else {
      for (const [key, value] of Object.entries(init.headers)) {
        if (typeof value === "string") headers.set(key, value);
      }
    }
  }
  // CRITICAL: set Authorization AFTER any caller-provided headers so
  // the helper's value always wins (defense against a misconfigured
  // caller passing a stale auth string in their headers map).
  headers.set("Authorization", buildCtmBasicAuthHeader(accessKey, secretKey));

  const result = await safeFetch(url, { ...init, headers }, CTM_TIMEOUT_MS);

  if (result.success) {
    return result;
  }

  // Error path — re-sanitize via CTM-specific helper.
  return {
    success: false,
    error: {
      ...result.error,
      message: sanitizeCtmErrorMessage(
        result.error.message,
        accessKey,
        secretKey,
      ),
      providerMessage: result.error.providerMessage
        ? sanitizeCtmErrorMessage(
            result.error.providerMessage,
            accessKey,
            secretKey,
          )
        : undefined,
    },
  };
}
